import { useEffect, useState } from "react";

import { io, Socket } from "socket.io-client";

import { getCookie } from "../utils/cookie";
import { CookieKey } from "./useCookie";

const SERVER_DISCONNECT_RETRY_DELAY_MS = 3000;

// One connection shared by every component, instead of one per page
let sharedSocket: Socket | null = null;

const getSharedSocket = (): Socket => {
  if (sharedSocket) return sharedSocket;

  const socket = io(process.env.REACT_APP_API_URL, {
    // Evaluated on every (re)connect so a refreshed JWT is picked up,
    // rather than the token that existed when the page first loaded
    auth: (cb) => cb({ token: getCookie(CookieKey.JWT_TOKEN) }),
  });

  // When the server rejects the handshake (missing/expired JWT) it calls
  // client.disconnect(), and socket.io does not auto-reconnect after that.
  // Retry; by then the HTTP client will usually have refreshed the JWT cookie.
  socket.on("disconnect", (reason) => {
    if (reason === "io server disconnect") {
      setTimeout(() => {
        if (!socket.connected) socket.connect();
      }, SERVER_DISCONNECT_RETRY_DELAY_MS);
    }
  });

  sharedSocket = socket;
  return socket;
};

export const useWebSocket = (): Socket | null => {
  // State (not a ref) so consumers re-render and attach listeners immediately
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    setSocket(getSharedSocket());
  }, []);

  return socket;
};
